import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';

import { CAROUSEL_SLIDES } from '../constants/carousel-slides';

export function SalesHeroCarousel(): ReactElement {
  const [viewportRef, api] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return undefined;
    setScrollSnaps(api.scrollSnapList());
    api.on('select', onSelect);
    onSelect();
    return () => {
      api.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <div className="vtwo:h-full vtwo:flex vtwo:flex-col vtwo:items-center vtwo:justify-center vtwo:py-8 vtwo:min-h-[320px] vtwo:bg-linear-to-b vtwo:from-featured-dark vtwo:to-featured-light">
      <div ref={viewportRef} className="vtwo:overflow-hidden vtwo:w-full vtwo:mb-4">
        <div className="vtwo:flex">
          {CAROUSEL_SLIDES.map((slide, i) => (
            <div
              key={i}
              className="vtwo:flex-none vtwo:w-full vtwo:flex vtwo:items-center vtwo:justify-center"
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="vtwo:max-w-[85%] vtwo:max-h-[240px] vtwo:h-auto vtwo:object-contain"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="vtwo:flex vtwo:px-8 vtwo:items-center vtwo:justify-between vtwo:w-full vtwo:gap-6 vtwo:mb-3">
        <button
          type="button"
          onClick={() => api?.scrollPrev()}
          aria-label="Previous slide"
          className="vtwo:size-8 vtwo:shrink-0 vtwo:rounded-full vtwo:bg-white vtwo:flex vtwo:items-center vtwo:justify-center vtwo:text-black vtwo:cursor-pointer vtwo:hover:text-white vtwo:hover:bg-white/30 vtwo:transition-colors vtwo:focus-ring"
        >
          <ChevronLeft className="vtwo:size-5" />
        </button>

        <div className="vtwo:text-center vtwo:text-white vtwo:min-w-[200px]">
          <p className="vtwo:text-xl vtwo:font-bold vtwo:mb-1.5">
            {CAROUSEL_SLIDES[selectedIndex].title}
          </p>
          <p className="vtwo:text-base">{CAROUSEL_SLIDES[selectedIndex].subtitle}</p>
        </div>

        <button
          type="button"
          onClick={() => api?.scrollNext()}
          aria-label="Next slide"
          className="vtwo:size-8 vtwo:shrink-0 vtwo:rounded-full vtwo:bg-white vtwo:flex vtwo:items-center vtwo:justify-center vtwo:text-black vtwo:cursor-pointer vtwo:hover:text-white vtwo:hover:bg-white/30 vtwo:transition-colors vtwo:focus-ring"
        >
          <ChevronRight className="vtwo:size-5" />
        </button>
      </div>

      <div className="vtwo:flex vtwo:px-8 vtwo:items-center vtwo:gap-2">
        {scrollSnaps.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => api?.scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`vtwo:size-2 vtwo:rounded-full vtwo:cursor-pointer vtwo:transition-colors vtwo:focus-ring ${
              i === selectedIndex ? 'vtwo:bg-white' : 'vtwo:bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
